import { apiClient as api } from "./httpClient";

// Diagnósticos (CID) do paciente (RN-535): aba restrita a Administrador e Profissional;
// quem vê a aba também cria/edita/exclui. Exclusão é LÓGICA (Active=false) e a escrita é
// dupla no Bubble (RN-302) — a lista lê o tenant.

export interface PatientDiagnosis {
  id: string;
  cidId: string | null;
  cidSubcategory: string;
  cidDescription: string;
  comments: string;
  active: boolean;
  createdByName: string;
  /** ISO no relógio da entidade (sem offset) */
  createdAt: string;
  modifiedAt: string | null;
  /** thread de comentários (RN-535d) */
  commentsCount: number;
  /** Autoria (RN-535g): quem inseriu o diagnóstico */
  isAuthor: boolean;
  /** Autor ou Administrador — quem não pode editar só comenta */
  canEdit: boolean;
  /** Administrador mexendo em registro de terceiro: justificativa obrigatória */
  requiresReason: boolean;
}

/** Comentário do diagnóstico; kind: comment | inactivation | reactivation */
export interface DiagnosisComment {
  id: string;
  comment: string;
  kind: string;
  createdByName: string;
  /** ISO no relógio da entidade */
  createdAt: string;
  isMine: boolean;
  /** autor E kind=comment — registros de inativação/reativação não saem */
  canDelete: boolean;
}

export interface PatientDiagnosesListResult {
  items: PatientDiagnosis[];
  totalCount: number;
}

export interface PatientDiagnosesQueryParams {
  /** 'active' (default) | 'inactive' | 'all' */
  status?: string;
  from?: string;
  to?: string;
  /** Busca por palavra no código do CID, na descrição e nas observações (RN-535e). */
  search?: string;
  pageNumber: number;
  pageSize: number;
}

export const DIAGNOSIS_COMMENTS_MAX_LENGTH = 2000;

export const patientDiagnosesService = {
  getByPatient: async (
    patientId: string, params: PatientDiagnosesQueryParams): Promise<PatientDiagnosesListResult> => {
    const response = await api.get(`/patient-diagnoses/by-patient/${patientId}`, { params });
    return response.data;
  },

  /** Bubble primeiro (~1s) — timeout largo de propósito */
  create: async (patientId: string, cidId: string, comments: string | null): Promise<string> => {
    const response = await api.post("/patient-diagnoses", { patientId, cidId, comments }, { timeout: 60000 });
    return response.data;
  },

  update: async (id: string, cidId: string, comments: string | null, reason?: string | null): Promise<void> => {
    await api.put(`/patient-diagnoses/${id}`, { cidId, comments, reason: reason ?? null }, { timeout: 60000 });
  },

  /** Inativação (Active=false): o registro vai para o filtro Inativos, não some da história.
   *  A justificativa (opcional) entra na thread de comentários como registro de inativação. */
  deactivate: async (id: string, reason: string | null): Promise<void> => {
    await api.delete(`/patient-diagnoses/${id}`, { data: { reason }, timeout: 60000 });
  },

  getComments: async (id: string): Promise<DiagnosisComment[]> => {
    const response = await api.get(`/patient-diagnoses/${id}/comments`);
    return response.data.items;
  },

  addComment: async (id: string, comment: string): Promise<string> => {
    const response = await api.post(`/patient-diagnoses/${id}/comments`, { comment }, { timeout: 60000 });
    return response.data;
  },

  /** Só o autor exclui o próprio comentário */
  deleteComment: async (commentId: string): Promise<void> => {
    await api.delete(`/patient-diagnoses/comments/${commentId}`, { timeout: 60000 });
  },

  /** Reativa um diagnóstico inativo; recusado se o mesmo CID já estiver ativo de novo */
  reactivate: async (id: string, reason?: string | null): Promise<void> => {
    await api.post(`/patient-diagnoses/${id}/reactivate`, { reason: reason ?? null }, { timeout: 60000 });
  },
};

/** Erros de negócio conhecidos em PT-BR; o code vem do Result.Error do backend. */
export const mapPatientDiagnosisError = (error: any): string => {
  const code: string = error?.response?.data?.code ?? "";
  const messages: Record<string, string> = {
    "PatientDiagnosis.NotFound": "Diagnóstico não encontrado.",
    "PatientDiagnosis.PatientNotFound": "Paciente não encontrado na entidade.",
    "PatientDiagnosis.CidRequired": "Selecione um CID do catálogo.",
    "PatientDiagnosis.CidNotFound": "CID não encontrado no catálogo.",
    "PatientDiagnosis.CommentsTooLong": `As observações devem ter no máximo ${DIAGNOSIS_COMMENTS_MAX_LENGTH} caracteres.`,
    "PatientDiagnosis.Duplicated": "Este CID já está registrado e ativo para o paciente.",
    "PatientDiagnosis.CommentRequired": "Escreva o texto do comentário.",
    "PatientDiagnosis.CommentNotFound": "Comentário não encontrado (pode ter sido excluído).",
    "PatientDiagnosis.CommentNotDeletable": "Somente quem escreveu pode excluir o comentário; registros de inativação e reativação não podem ser excluídos.",
    "PatientDiagnosis.BubbleWriteFailed": "Não foi possível gravar o diagnóstico agora. Tente novamente em instantes.",
    "PatientDiagnosis.OnlyAuthorOrAdmin":
      "Só quem registrou este diagnóstico pode alterá-lo ou inativá-lo. Você pode deixar um comentário.",
    "PatientDiagnosis.ReasonRequired":
      "Informe a justificativa para alterar um diagnóstico registrado por outra pessoa.",
  };
  return messages[code] || "Não foi possível concluir a operação. Tente novamente.";
};
